export type DeepLinkPlatform = 'ANDROID' | 'IOS' | 'WEB' | 'APP';

export type DeepLinkTargetAppKey = 'user' | 'plus';

export type DeepLinkCampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'ACTIVE'
  | 'PAUSED'
  | 'EXPIRED'
  | 'ARCHIVED';

export type DeepLinkParamSchema = {
  name: string;
  type: 'string' | 'number' | 'boolean';
  required?: boolean;
  label?: string;
};

export type DeepLinkRoute = {
  id: string;
  slug: string;
  label: string;
  screenName?: string | null;
  appRoute?: string | null;
  webPath?: string | null;
  requiresAuth: boolean;
  paramSchema?: DeepLinkParamSchema[] | null;
  minAppVersion?: string | null;
  maxAppVersion?: string | null;
  isActive: boolean;
  sortOrder: number;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
};

export type DeepLinkShortCodeLookupKind =
  | 'campaign'
  | 'intent'
  | 'expired_intent'
  | 'not_found';

export type DeepLinkShortCodeLookup = {
  kind: DeepLinkShortCodeLookupKind | 'open' | 'unknown' | 'empty';
  shortCode?: string;
  prefix?: string | null;
  appKey?: string | null;
  liveUrl?: string | null;
  valid: boolean;
  message: string;
  publicCheckUrl?: string | null;
  inspectHint?: string | null;
  parsed?: {
    type?: string;
    href?: string;
    prefix?: string | null;
    shortCode?: string;
    query?: Record<string, string>;
    screen?: string | null;
  };
  resolvedRouteParams?: Record<string, string> | null;
  routeParams?: Record<string, string> | null;
  intent?: {
    shortCode: string;
    campaignId: string;
    targetAppKey?: string;
    routeParams?: Record<string, string> | null;
    source?: string | null;
    expiresAt?: string | null;
    redeemedCount?: number;
    createdAt?: string;
  } | null;
  campaign?: {
    id: string;
    name: string;
    shortCode?: string | null;
    shortUrl?: string | null;
    routeSlug?: string | null;
    status?: string | null;
    routeParams?: Record<string, string> | null;
    targetAppKey?: string | null;
  } | null;
  route?: {
    slug?: string;
    label?: string;
    webPath?: string | null;
    appRoute?: string | null;
    requiresAuth?: boolean;
    isActive?: boolean;
  } | null;
  intended?: {
    routeSlug?: string | null;
    webPath?: string | null;
    webUrl?: string | null;
    appRoute?: string | null;
    appArgs?: {
      speciality?: string | null;
      specialityId?: string | null;
      consultationType?: string | null;
    };
    loginRequired?: boolean;
  } | null;
  diagnose?: {
    issues?: string[];
    notes?: string[];
  };
};

export type DeepLinkCampaign = {
  id: string;
  name: string;
  description?: string | null;
  routeId: string;
  routeSlug: string;
  routeParams?: Record<string, string> | null;
  targetPlatforms: DeepLinkPlatform[];
  targetAppKey?: DeepLinkTargetAppKey;
  loginRequired: boolean;
  status: DeepLinkCampaignStatus;
  startsAt?: string | null;
  expiresAt?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  minAppVersion?: string | null;
  maxAppVersion?: string | null;
  shortCode?: string | null;
  fullUrl?: string | null;
  shortUrl?: string | null;
  totalClicks: number;
  totalInstalls: number;
  totalLogins: number;
  totalBookings: number;
  route?: DeepLinkRoute;
  metadata?: Record<string, unknown> | null;
  whatsappUsage?: DeepLinkWhatsAppUsage;
  createdAt?: string;
  updatedAt?: string;
};

export type DeepLinkWhatsAppUsage = {
  used: boolean;
  total: number;
  notifications: Array<{
    id: string;
    eventKey: string;
    label: string;
    templateName?: string | null;
    whatsappEnabled?: boolean;
    viaCta?: boolean;
    viaVariables?: boolean;
  }>;
  templates: Array<{
    id: string;
    name: string;
    displayName: string;
    isActive?: boolean;
  }>;
  campaigns: Array<{
    id: string;
    name: string;
    templateName?: string | null;
    status?: string;
  }>;
};

export type DeepLinkAnalyticsSummary = {
  totalClicks: number;
  installs: number;
  logins: number;
  bookings: number;
  appOpens: number;
  screenViews: number;
  webOpens?: number;
  androidClicks?: number;
  iosClicks?: number;
  appClicks?: number;
  clicksByPlatform?: Record<string, number>;
  clicksByResolvedAction?: Record<string, number>;
  clickToInstallRate: number;
  installToLoginRate: number;
  loginToBookingRate: number;
  navigationSuccesses?: number;
  navigationMismatches?: number;
  navigationErrors?: number;
  conversionsByType: Record<string, number>;
};

export type DeepLinkFunnel = {
  stages: { name: string; value: number }[];
  rates: {
    clickToInstall: number;
    installToLogin: number;
    loginToBooking: number;
  };
};

export type DeepLinkCampaignAnalyticsRow = {
  campaignId: string;
  clicks: number;
  installs: number;
  logins: number;
  appOpens: number;
  navigationSuccesses: number;
  navigationMismatches: number;
  navigationErrors: number;
};

export type DeepLinkNavigationEvent = {
  id: string;
  eventType: string;
  platform?: string | null;
  createdAt?: string;
  metadata?: Record<string, unknown> | null;
  intendedPath?: string | null;
  actualPath?: string | null;
  reason?: string | null;
};

export type DeepLinkRecentClick = {
  id: string;
  clickId: string;
  platform?: string | null;
  resolvedAction?: string | null;
  routeSlug?: string | null;
  createdAt?: string;
  metadata?: Record<string, unknown> | null;
};

export type DeepLinkCampaignAnalyticsDetail = {
  summary: DeepLinkAnalyticsSummary;
  recentNavigationEvents: DeepLinkNavigationEvent[];
  recentClicks: DeepLinkRecentClick[];
};

export type DeepLinkCampaignLeaderboardRow = {
  campaignId: string;
  clicks: number;
  campaign: Pick<
    DeepLinkCampaign,
    | 'id'
    | 'name'
    | 'routeSlug'
    | 'shortCode'
    | 'totalClicks'
    | 'totalInstalls'
    | 'totalLogins'
    | 'totalBookings'
    | 'status'
  > | null;
};

export type DeepLinkCampaignFormValues = {
  name: string;
  description?: string;
  routeId: string;
  routeParams?: Record<string, string>;
  targetPlatforms: DeepLinkPlatform[];
  targetAppKey?: DeepLinkTargetAppKey;
  loginRequired: boolean;
  status: DeepLinkCampaignStatus;
  startsAt?: string;
  expiresAt?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  minAppVersion?: string;
  maxAppVersion?: string;
};

export type DeepLinkRouteFormValues = {
  slug: string;
  label: string;
  screenName?: string;
  appRoute?: string;
  webPath?: string;
  requiresAuth: boolean;
  paramSchema?: DeepLinkParamSchema[];
  minAppVersion?: string;
  maxAppVersion?: string;
  isActive: boolean;
  sortOrder?: number;
};

export type DeepLinkScreenCatalogItem = {
  screenName: string;
  service: string;
  serviceLabel: string;
  label: string;
  appRoute: string;
  webPath?: string | null;
  notes?: string | null;
  routeId: string | null;
  slug: string;
  requiresAuth: boolean;
  paramSchema?: DeepLinkParamSchema[] | null;
  isActive: boolean;
  hasDbRoute: boolean;
  platform: 'app';
};

/** Plus doctor dashboard tabs share one Flutter route; backend sends initialIndex in routeParams. */
const PLUS_VENDOR_DOCTOR_TAB_HINTS: Record<string, string> = {
  'vendor-doctor-dashboard': 'dashboard tab (initialIndex 0)',
  'vendor-doctor-bookings': 'bookings tab (initialIndex 1)',
  'vendor-doctor-meeting': 'bookings / meeting tab (initialIndex 1)',
  'vendor-doctor-history': 'history tab (initialIndex 2)',
  'vendor-doctor-profile': 'profile tab (initialIndex 4)',
  'vendor-doctor-settlement': 'dashboard + settlement drawer (initialIndex 0)',
};

export function describeAppDeepLinkDestination(screen: DeepLinkScreenCatalogItem): string {
  const tabHint = PLUS_VENDOR_DOCTOR_TAB_HINTS[screen.slug];
  if (tabHint && screen.appRoute === '/VendorClinicDashBoard') {
    return `${screen.appRoute} → ${tabHint}`;
  }
  if (screen.notes?.trim()) {
    return `${screen.appRoute} (${screen.notes.trim()})`;
  }
  return screen.appRoute;
}

export type DeepLinkServiceOption = {
  key: string;
  label: string;
  screenCount: number;
};

export type DeepLinkScreenCatalog = {
  services: DeepLinkServiceOption[];
  screensByService: Record<string, DeepLinkScreenCatalogItem[]>;
  serviceLabels: Record<string, string>;
  targetAppKey?: DeepLinkTargetAppKey;
  summary?: {
    totalScreens: number;
    seededCount: number;
    pendingCount: number;
  };
  meta?: {
    flutterRoutesLoaded?: boolean;
    filter?: string;
  };
};

export type DeepLinkEnvironmentConfig = {
  domain: string;
  isProduction: boolean;
  openPath: string;
  shortPath: string;
  productionDomain: string;
  localDomain: string;
  exampleOpenUrl: string;
  exampleShortUrl: string;
  storeUrls?: Record<
    DeepLinkTargetAppKey,
    {
      label: string;
      androidPackageId: string;
      playStoreUrl: string;
      appStoreUrl: string;
    }
  >;
};

export const DEEP_LINK_TARGET_APP_OPTIONS: {
  value: DeepLinkTargetAppKey;
  label: string;
}[] = [
  { value: 'user', label: 'Vedika.Health (User App)' },
  { value: 'plus', label: 'Vedika.Health Plus (Service Provider App)' },
];

export const DEEP_LINK_PLATFORM_OPTIONS: { value: DeepLinkPlatform; label: string }[] = [
  { value: 'ANDROID', label: 'Android' },
  { value: 'IOS', label: 'iOS' },
  { value: 'WEB', label: 'Web' },
  { value: 'APP', label: 'All mobile apps (Android + iOS)' },
];

export const DEEP_LINK_STATUS_OPTIONS: { value: DeepLinkCampaignStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'ARCHIVED', label: 'Archived' },
];

export type DeepLinkConsultationMode = 'online' | 'in-clinic';

export type DeepLinkSeededCampaignSummary = {
  id: string;
  name: string;
  shortCode?: string | null;
  shortUrl?: string | null;
  fullUrl?: string | null;
  status?: string;
  routeSlug?: string;
  routeParams?: Record<string, string> | null;
  metadata?: Record<string, unknown> | null;
};

export type DeepLinkDoctorModeSeed = {
  available: boolean;
  seeded: boolean;
  campaign: DeepLinkSeededCampaignSummary | null;
};

export type DeepLinkDoctorSeedItem = {
  vendorId: string;
  doctorName: string;
  clinicName?: string | null;
  city?: string | null;
  citySlug: string;
  doctorSlug: string;
  consultationTypes: string[];
  availableModes: DeepLinkConsultationMode[];
  modes: Record<DeepLinkConsultationMode, DeepLinkDoctorModeSeed>;
  seedStatus: 'pending' | 'partial' | 'done';
  seededCount: number;
  availableCount: number;
};

export type DeepLinkDoctorSeedList = {
  items: DeepLinkDoctorSeedItem[];
  total: number;
  pendingDoctors: number;
  doneDoctors: number;
  seededCampaigns: number;
};

export type DeepLinkBlogSeedItem = {
  blogId: string;
  title: string;
  slug?: string | null;
  authorId?: string | null;
  authorType?: string | null;
  blogDate?: string | null;
  seeded: boolean;
  seedStatus: 'pending' | 'done';
  campaign: DeepLinkSeededCampaignSummary | null;
};

export type DeepLinkBlogSeedList = {
  items: DeepLinkBlogSeedItem[];
  total: number;
  pendingBlogs: number;
  doneBlogs: number;
  seededCampaigns: number;
};
